import React from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { Gauge, Thermometer, Droplets, Zap, Timer, Shield, Download, Eye } from 'lucide-react'

const Specifications = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const technicalSpecs = [
    {
      icon: Gauge,
      category: "Performance",
      specs: [
        { label: "Light Output", value: "500 Lux", unit: "at 1 meter" },
        { label: "Charging Time", value: "5 seconds", unit: "any light source" },
        { label: "Operating Duration", value: "12+ hours", unit: "continuous" },
        { label: "Efficiency", value: "95%", unit: "energy conversion" }
      ]
    },
    {
      icon: Thermometer,
      category: "Environmental",
      specs: [
        { label: "Operating Temperature", value: "-20°C to +60°C", unit: "(-4°F to 140°F)" },
        { label: "Storage Temperature", value: "-40°C to +80°C", unit: "(-40°F to 176°F)" },
        { label: "Humidity Resistance", value: "95% RH", unit: "non-condensing" },
        { label: "UV Resistance", value: "UV-8", unit: "rating" }
      ]
    },
    {
      icon: Droplets,
      category: "Durability",
      specs: [
        { label: "IP Rating", value: "IP67", unit: "waterproof" },
        { label: "Impact Resistance", value: "IK08", unit: "vandal resistant" },
        { label: "Cycle Life", value: "50,000+", unit: "charge cycles" },
        { label: "Warranty", value: "5 years", unit: "full coverage" }
      ]
    },
    {
      icon: Zap,
      category: "Electrical",
      specs: [
        { label: "Power Consumption", value: "0 Watts", unit: "operation" },
        { label: "Voltage", value: "N/A", unit: "self-powered" },
        { label: "Current Draw", value: "0 Amps", unit: "no electrical input" },
        { label: "Surge Protection", value: "N/A", unit: "no electronics" }
      ]
    }
  ]

  const certifications = [
    { name: "CE Marking", description: "European Conformity" },
    { name: "RoHS Compliant", description: "Restriction of Hazardous Substances" },
    { name: "ISO 9001", description: "Quality Management Systems" },
    { name: "IES LM-79", description: "Photometric Testing Standard" },
    { name: "UL Listed", description: "Underwriters Laboratories" },
    { name: "FCC Part 15", description: "Electromagnetic Compliance" }
  ]

  const productDimensions = [
    { model: "PGM-Panel-S", size: "30cm × 30cm × 2cm", weight: "0.8 kg", coverage: "2 m²" },
    { model: "PGM-Panel-M", size: "60cm × 60cm × 2cm", weight: "2.1 kg", coverage: "8 m²" },
    { model: "PGM-Panel-L", size: "120cm × 60cm × 2cm", weight: "3.8 kg", coverage: "15 m²" },
    { model: "PGM-Strip-5m", size: "500cm × 5cm × 1cm", weight: "1.2 kg", coverage: "Linear" }
  ]

  return (
    <section id="specifications" className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            Technical <span className="text-purple-500">Specifications</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Detailed technical specifications and performance data for Pigmentium™ lighting systems. 
            All specifications are tested and verified under controlled laboratory conditions.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {technicalSpecs.map((category, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl flex items-center justify-center mr-4">
                  <category.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800">{category.category}</h3>
              </div>
              <div className="space-y-4">
                {category.specs.map((spec, idx) => (
                  <div key={idx} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-700 font-medium">{spec.label}</span>
                    <div className="text-right">
                      <span className="text-lg font-bold text-gray-800">{spec.value}</span>
                      <div className="text-sm text-gray-500">{spec.unit}</div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="bg-white rounded-2xl p-8 shadow-lg mb-16"
        >
          <h3 className="text-3xl font-bold text-center text-gray-800 mb-8">Product Models & Dimensions</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="pb-4 text-lg font-semibold text-gray-800">Model</th>
                  <th className="pb-4 text-lg font-semibold text-gray-800">Dimensions</th>
                  <th className="pb-4 text-lg font-semibold text-gray-800">Weight</th>
                  <th className="pb-4 text-lg font-semibold text-gray-800">Coverage</th>
                  <th className="pb-4 text-lg font-semibold text-gray-800">Action</th>
                </tr>
              </thead>
              <tbody>
                {productDimensions.map((product, index) => (
                  <motion.tr
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={inView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.6, delay: 1 + index * 0.1 }}
                    className="border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200"
                  >
                    <td className="py-4 font-medium text-gray-800">{product.model}</td>
                    <td className="py-4 text-gray-600">{product.size}</td>
                    <td className="py-4 text-gray-600">{product.weight}</td>
                    <td className="py-4 text-gray-600">{product.coverage}</td>
                    <td className="py-4">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 transition-colors duration-200"
                      >
                        View Details
                      </motion.button>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="bg-gradient-to-br from-purple-500 to-blue-600 rounded-2xl p-8 text-white"
          >
            <Shield className="w-12 h-12 mb-4" />
            <h3 className="text-2xl font-bold mb-4">Certifications & Standards</h3>
            <div className="grid grid-cols-2 gap-4">
              {certifications.map((cert, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-lg rounded-lg p-3">
                  <div className="font-semibold">{cert.name}</div>
                  <div className="text-sm opacity-90">{cert.description}</div>
                </div>
              ))}
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 1.4 }}
            className="bg-white rounded-2xl p-8 shadow-lg"
          >
            <h3 className="text-2xl font-bold text-gray-800 mb-6">Documentation & Resources</h3>
            <div className="space-y-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-200"
              >
                <div className="flex items-center">
                  <Download className="w-5 h-5 text-purple-500 mr-3" />
                  <span className="font-medium text-gray-800">Technical Datasheet (PDF)</span>
                </div>
                <span className="text-sm text-gray-500">2.3 MB</span>
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-200"
              >
                <div className="flex items-center">
                  <Download className="w-5 h-5 text-purple-500 mr-3" />
                  <span className="font-medium text-gray-800">Installation Guide (PDF)</span>
                </div>
                <span className="text-sm text-gray-500">1.8 MB</span>
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-200"
              >
                <div className="flex items-center">
                  <Eye className="w-5 h-5 text-purple-500 mr-3" />
                  <span className="font-medium text-gray-800">3D Product Models (CAD)</span>
                </div>
                <span className="text-sm text-gray-500">5.1 MB</span>
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-200"
              >
                <div className="flex items-center">
                  <Download className="w-5 h-5 text-purple-500 mr-3" />
                  <span className="font-medium text-gray-800">Compliance Certificates</span>
                </div>
                <span className="text-sm text-gray-500">892 KB</span>
              </motion.button>
            </div>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="w-full mt-6 bg-gradient-to-r from-purple-500 to-blue-600 text-white font-semibold py-3 px-6 rounded-xl hover:shadow-lg transition-all duration-300"
            >
              Request Complete Technical Package
            </motion.button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1.6 }}
          className="text-center bg-gradient-to-r from-yellow-400 to-orange-500 rounded-2xl p-8 text-white"
        >
          <Timer className="w-16 h-16 mx-auto mb-4" />
          <h3 className="text-3xl font-bold mb-4">Need Custom Specifications?</h3>
          <p className="text-xl mb-6 opacity-90">
            Our engineering team can customize Pigmentium™ products to meet your specific technical requirements 
            including custom sizes, mounting options, and performance characteristics.
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-white text-orange-500 font-semibold py-3 px-8 rounded-xl hover:shadow-lg transition-all duration-300"
          >
            Discuss Custom Requirements
          </motion.button>
        </motion.div>
      </div>
    </section>
  )
}

export default Specifications 